import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import React from "react";
import { ChangeLogView } from "../../change-log/ChangeLogView.js";
import {
  type ChangeLogFilterState,
  CHANGE_LOG_PAGE_SIZE,
  EMPTY_CHANGE_LOG_FILTERS,
  toChangeLogQueryFilters,
} from "../../change-log/changeLogTypes.js";
import {
  useQuoteChangeLogFacetsQuery,
  useQuoteChangeLogQuery,
} from "./ChangeLogView.generated.js";
import { useQuoteEntityStrategy } from "./useQuoteEntityStrategy.js";

interface Props {
  projectId: string;
  docId: string;
  /** Quote doc path, e.g. `/projects/:projectId/quotes/:quoteId` — for item links. */
  pathToDoc: string;
}

/**
 * Quote consumer of the shared `ChangeLogView`: owns the filter state and the
 * `quoteChangeLog` / `quoteChangeLogFacets` data source, and supplies the quote
 * entity-render strategy. Presentation lives entirely in the shared view.
 */
export function QuoteChangeLogView({ projectId, docId, pathToDoc }: Props) {
  const client = useApolloClient();

  const [filters, setFilters] = React.useState<ChangeLogFilterState>(
    EMPTY_CHANGE_LOG_FILTERS
  );
  const queryFilters = toChangeLogQueryFilters(filters);

  const facetsQuery = useQuoteChangeLogFacetsQuery({
    client,
    variables: { docId, projectId },
  });
  const logQuery = useQuoteChangeLogQuery({
    client,
    variables: {
      docId,
      projectId,
      filters: queryFilters,
      first: CHANGE_LOG_PAGE_SIZE,
    },
    notifyOnNetworkStatusChange: true,
  });

  const connection = getDataOrNull(logQuery.data?.quoteChangeLog);
  const facets = getDataOrNull(facetsQuery.data?.quoteChangeLogFacets);
  const missingPermissions =
    logQuery.data?.quoteChangeLog.__typename === "MissingPermissions";

  const entityStrategy = useQuoteEntityStrategy(pathToDoc);
  const pageInfo = connection?.pageInfo;

  return (
    <ChangeLogView
      entries={(connection?.edges ?? []).map(e => e.node)}
      facets={facets}
      loading={logQuery.loading}
      error={logQuery.error?.message}
      missingPermissions={missingPermissions}
      hasNextPage={Boolean(pageInfo?.hasNextPage)}
      onFetchMore={async () => {
        if (!pageInfo?.hasNextPage) return;
        await logQuery.fetchMore({
          variables: { after: pageInfo.endCursor, first: CHANGE_LOG_PAGE_SIZE },
        });
      }}
      filters={filters}
      setFilters={setFilters}
      entityStrategy={entityStrategy}
    />
  );
}
